import React, { useState } from 'react';
import useUserSubscription from '../hooks/useUserSubscription';
import { useParams } from 'react-router-dom';
import { sportConfigs } from '../data/sportConfigs';
import { getSkillsBySport } from '../data/new-skills';
import SportGrid from './SportGrid';
import { useSelectedPlayers } from '../contexts/SelectedPlayersContext';
import SimpleModal from './SimpleModal';
import PayPalHiddenTrigger from './PayPalHiddenTrigger';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import Button from './Button';
import { KiddiFitWordmark } from './Brand';
import AchievementToast from './AchievementToast';

const SkillDrillsSelection = () => {
  const { sport } = useParams();
  const { selectedPlayersData, removePlayer, addPlayer, allPlayersData } =
    useSelectedPlayers();
  const { isPremium } = useUserSubscription();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activated, setActivated] = useState(false);
  const [showEquipmentGuide, setShowEquipmentGuide] = useState(true);

  // Check localStorage on mount
  React.useEffect(() => {
    // Temporarily clear localStorage for debugging
    localStorage.removeItem(`${sport}-equipment-dismissed`);

    const dismissed = localStorage.getItem(`${sport}-equipment-dismissed`);
    if (dismissed === 'true') {
      setShowEquipmentGuide(false);
    }
  }, [sport]);

  const handleEquipmentDismiss = () => {
    setShowEquipmentGuide(false);
    localStorage.setItem(`${sport}-equipment-dismissed`, 'true');
  };

  const handleShowEquipment = () => {
    setShowEquipmentGuide(true);
    localStorage.removeItem(`${sport}-equipment-dismissed`);
  };

  // Get sorted skills based on player count
  const playerCount = selectedPlayersData.length;
  const skills = getSkillsBySport(sport);

  const sportConfig = sportConfigs[sport];

  const sportData = {
    soccer: {
      name: 'Soccer',
      icon: '⚽',
      color: '#4CAF50',
    },
    basketball: {
      name: 'Basketball',
      icon: '🏀',
      color: '#FF9800',
    },
    swimming: {
      name: 'Swimming',
      icon: '🏊',
      color: '#2196F3',
    },
    tennis: {
      name: 'Tennis',
      icon: '🎾',
      color: '#9C27B0',
    },
    volleyball: {
      name: 'Volleyball',
      icon: '🏐',
      color: '#F44336',
    },
    football: {
      name: 'Football',
      icon: '🏈',
      color: '#795548',
    },
    baseball: {
      name: 'Baseball',
      icon: '⚾',
      color: '#FF5722',
    },
    track: {
      name: 'Track & Field',
      icon: '🏃',
      color: '#607D8B',
    },

    martial_arts: {
      name: 'Martial Arts',
      icon: '🥋',
      color: '#3F51B5',
    },
    dance: {
      name: 'Dance',
      icon: '💃',
      color: '#FF4081',
    },
    lacrosse: {
      name: 'Lacrosse',
      icon: '🥍',
      color: '#8BC34A',
    },
    rugby: {
      name: 'Rugby',
      icon: '🏉',
      color: '#FF9800',
    },
    cricket: {
      name: 'Cricket',
      icon: '🏏',
      color: '#795548',
    },
    golf: {
      name: 'Golf',
      icon: '⛳',
      color: '#4CAF50',
    },
    hockey: {
      name: 'Hockey',
      icon: '🏒',
      color: '#2196F3',
    },
    karate: {
      name: 'Karate',
      icon: '🥋',
      color: '#3F51B5',
    },
    taekwondo: {
      name: 'Taekwondo',
      icon: '☯',
      color: '#3F51B5',
    },
    'martial-arts': {
      name: 'Martial Arts',
      icon: '🥷🏼',
      color: '#3F51B5',
    },
    ballet: {
      name: 'Ballet',
      icon: '🩰',
      color: '#E91E63',
    },
    boxing: {
      name: 'Boxing',
      icon: '🥊',
      color: '#FF5722',
    },
    yoga: {
      name: 'Yoga',
      icon: '🧘🏻‍♀️',
      color: '#4CAF50',
    },
    'movement-coordination': {
      name: 'Gymnastics',
      icon: '🤸',
      color: '#E91E63',
    },
    'running-technique': {
      name: 'Running Technique',
      icon: '🏃',
      color: '#607D8B',
    },
  };

  const currentSport = sportData[sport] || {
    name: sport.charAt(0).toUpperCase() + sport.slice(1).replace('_', ' '),
    icon: '🏆',
    color: '#666',
  };

  // Allow rendering even if sport config is missing; we still have drills and can show them

  // Create skill items from the new skills system
  const rawItems = skills
    .map((skill) => {
      // Check if this is a partner skill based on emoji
      const hasPartnerDrills = skill.emoji === '👥';
      const isPartnerDisabled = hasPartnerDrills && playerCount === 1;

      // Lock all but first skill if not premium
      const index = skills.findIndex((s) => s.skillId === skill.skillId);
      const basePremiumLocked = !isPremium && index > 0;

      // If not premium, treat any disabled state as premium-locked for UX
      const isDisabled = isPartnerDisabled || basePremiumLocked;
      const isPremiumLocked = !isPremium && isDisabled;

      return {
        icon: skill.emoji || '👤', // Use emoji from skill data, fallback to individual
        title: skill.title,
        subtitle:
          !isPremium && isDisabled
            ? '🔒 Premium required'
            : isPartnerDisabled
            ? 'Partner required - add another player to unlock'
            : `${skill.duration} • ${skill.points} points`,
        color: isDisabled ? '#ccc' : currentSport.color,
        link: isDisabled ? null : `${sport}/${skill.skillId}`,
        disabled: isDisabled,
        premiumLocked: isPremiumLocked,
        isPartner: hasPartnerDrills,
        skillId: skill.skillId,
      };
    })
    .sort((a, b) => a.skillId - b.skillId);

  // If more than one player, show partner skills first by splitting, sorting each, then merging
  const skillItems = (() => {
    if (playerCount > 1) {
      const partners = rawItems
        .filter((i) => i.isPartner)
        .sort((a, b) => a.skillId - b.skillId);
      const individuals = rawItems
        .filter((i) => !i.isPartner)
        .sort((a, b) => a.skillId - b.skillId);
      return [...partners, ...individuals];
    }
    // Single player: plain id ascending
    return rawItems;
  })();

  // Equipment data for each sport (only for sports that need equipment)
  const equipmentData = {
    soccer: {
      title: 'Soccer Equipment Guide',
      content: `
        <table style="width: 100%; border-collapse: collapse; font-size: 0.9rem;">
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #4CAF50; width: 80px;">Essential:</td>
            <td style="padding: 4px 8px;">Small foam ball (indoor), Soccer ball (outdoor), Partner for team skills</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #FF9800; width: 80px;">Optional:</td>
            <td style="padding: 4px 8px;">Wall space for passing and shooting</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #2196F3; width: 80px;">Safety:</td>
            <td style="padding: 4px 8px;">Use foam balls indoors to protect walls and furniture</td>
          </tr>
        </table>
      `,
    },
    basketball: {
      title: 'Basketball Equipment Guide',
      content: `
        <table style="width: 100%; border-collapse: collapse; font-size: 0.9rem;">
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #4CAF50; width: 80px;">Essential:</td>
            <td style="padding: 4px 8px;">Basketball, Soft ball (indoor), Wall space for dribbling</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #FF9800; width: 80px;">Optional:</td>
            <td style="padding: 4px 8px;">Basketball hoop</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #2196F3; width: 80px;">Safety:</td>
            <td style="padding: 4px 8px;">Use soft balls indoors to protect walls and furniture</td>
          </tr>
        </table>
      `,
    },
    baseball: {
      title: 'Baseball Equipment Guide',
      content: `
        <table style="width: 100%; border-collapse: collapse; font-size: 0.9rem;">
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #4CAF50; width: 80px;">Essential:</td>
            <td style="padding: 4px 8px;">Soft ball or rolled socks, Gloves (optional)</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #FF9800; width: 80px;">Optional:</td>
            <td style="padding: 4px 8px;">Wall space for throwing, Open area for catching</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #2196F3; width: 80px;">Safety:</td>
            <td style="padding: 4px 8px;">Use soft balls to prevent injury, Practice in safe area</td>
          </tr>
        </table>
      `,
    },
    volleyball: {
      title: 'Volleyball Equipment Guide',
      content: `
        <table style="width: 100%; border-collapse: collapse; font-size: 0.9rem;">
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #4CAF50; width: 80px;">Essential:</td>
            <td style="padding: 4px 8px;">Volleyball, Soft ball (indoor), Partner for team skills</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #FF9800; width: 80px;">Optional:</td>
            <td style="padding: 4px 8px;">Wall space for serving and passing</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #2196F3; width: 80px;">Safety:</td>
            <td style="padding: 4px 8px;">Use soft balls indoors to protect walls and furniture</td>
          </tr>
        </table>
      `,
    },
    tennis: {
      title: 'Tennis Equipment Guide',
      content: `
        <table style="width: 100%; border-collapse: collapse; font-size: 0.9rem;">
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #4CAF50; width: 80px;">Essential:</td>
            <td style="padding: 4px 8px;">Tennis racket, Tennis ball, Soft ball (indoor)</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #FF9800; width: 80px;">Optional:</td>
            <td style="padding: 4px 8px;">Wall space for practice, Partner for games</td>
          </tr>
          <tr>
            <td style="padding: 4px 8px; font-weight: 600; color: #2196F3; width: 80px;">Safety:</td>
            <td style="padding: 4px 8px;">Use soft balls indoors to protect walls and furniture</td>
          </tr>
        </table>
      `,
    },
    karate: {
      title: 'Karate Equipment Guide',
      content: null, // No equipment needed
    },
    dance: {
      title: 'Dance Equipment Guide',
      content: null, // No equipment needed
    },
    yoga: {
      title: 'Yoga Equipment Guide',
      content: null, // No equipment needed
    },
    ballet: {
      title: 'Ballet Equipment Guide',
      content: null, // No equipment needed
    },
    taekwondo: {
      title: 'Taekwondo Equipment Guide',
      content: null, // No equipment needed
    },
    gymnastics: {
      title: 'Gymnastics Equipment Guide',
      content: null, // No equipment needed
    },
    'running-technique': {
      title: 'Running Technique Equipment Guide',
      content: null, // No equipment needed
    },
    'martial-arts': {
      title: 'Martial Arts Equipment Guide',
      content: null, // No equipment needed
    },
    'movement-coordination': {
      title: 'Movement Coordination Equipment Guide',
      content: null, // No equipment needed
    },
    swimming: {
      title: 'Swimming Equipment Guide',
      content: null, // No equipment needed
    },
    football: {
      title: 'Football Equipment Guide',
      content: null, // No equipment needed
    },
    lacrosse: {
      title: 'Lacrosse Equipment Guide',
      content: null, // No equipment needed
    },
    rugby: {
      title: 'Rugby Equipment Guide',
      content: null, // No equipment needed
    },
    cricket: {
      title: 'Cricket Equipment Guide',
      content: null, // No equipment needed
    },
    golf: {
      title: 'Golf Equipment Guide',
      content: null, // No equipment needed
    },
    hockey: {
      title: 'Hockey Equipment Guide',
      content: null, // No equipment needed
    },
    boxing: {
      title: 'Boxing Equipment Guide',
      content: null, // No equipment needed
    },
    track: {
      title: 'Track Equipment Guide',
      content: null, // No equipment needed
    },
    martial_arts: {
      title: 'Martial Arts Equipment Guide',
      content: null, // No equipment needed
    },
  };

  const equipmentInfo = equipmentData[sport];

  console.log('Equipment guide debug:', {
    showEquipmentGuide,
    sport,
    equipmentInfo,
    dismissed: localStorage.getItem(`${sport}-equipment-dismissed`),
  });

  const equipmentGuideData =
    showEquipmentGuide && equipmentInfo && equipmentInfo.content
      ? {
          title: equipmentInfo.title,
          content: equipmentInfo.content,
          sport: sport,
          onClose: handleEquipmentDismiss,
        }
      : null;

  console.log(
    'Equipment guide data being passed to SportGrid:',
    equipmentGuideData
  );

  return (
    <>
      <SportGrid
        title={`${currentSport.name} Skills`}
        subtitle={`Choose a skill to play${
          playerCount > 1 ? ' — partner skills available' : ''
        }`}
        items={skillItems}
        showBackButton={true}
        backButtonText='← Back to Sports'
        backButtonPath='/skills/sports'
        itemLinkPrefix='/skills/sports/'
        onLockedClick={() => setShowUpgrade(true)}
        selectedPlayers={selectedPlayersData}
        onRemovePlayer={removePlayer}
        onAddPlayer={addPlayer}
        allPlayers={allPlayersData}
        equipmentGuide={
          showEquipmentGuide && equipmentInfo && equipmentInfo.content
            ? {
                title: equipmentInfo.title,
                content: equipmentInfo.content,
                sport: sport,
                onClose: handleEquipmentDismiss,
              }
            : null
        }
        showEquipmentGuide={showEquipmentGuide && equipmentInfo && equipmentInfo.content}
        onShowEquipment={handleShowEquipment}
      />

      <PremiumUpgradeModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
      />
    </>
  );
};

export default SkillDrillsSelection;
